'use client';

import React from 'react';

import { Button } from '@workspace/ui/components/customButton';

import { defaultFeatures } from '~/constants/homepage/features';
import type { FeatureSectionProps } from '~/types/feature-type';
import { BlurFade } from '../fragments/blur-fade';
import { SiteHeading } from '../fragments/site-heading';
import { FeatureCard } from './feature-card';

const AIPoweredFeaturesSection: React.FC<FeatureSectionProps> = ({
  title = 'Everything You Need To Create AI Tutorial Videos',
  description = 'Transform silent recordings into polished AI generated tutorial videos with realistic voiceovers using our simple, automated workflow.',
  badge,
  features = defaultFeatures
}) => {
  // Determine layout based on number of features
  const is8CardLayout = features.length === 8;

  // For 8 cards: 3-2-3 layout
  // For 7 cards (default): 3-2-2 layout
  const row1 = features.slice(0, 3);
  const row2 = features.slice(3, 5);
  const row3 = is8CardLayout ? features.slice(5, 8) : features.slice(5, 7);
  return (
    <section className="bg-[#FDFFF7] dark:bg-background">
      <div className="container">
        {/* Header */}
        <SiteHeading
          badge={badge}
          title={title}
          isSecondary
          description={description}
        />

        {/* Row 1: 3 cards */}
        <div className="grid grid-cols-1 gap-6 mb-6 md:grid-cols-2 lg:grid-cols-3">
          {row1.map((feature, index) => (
            <BlurFade
              key={index}
              inView
              delay={0.1 + index * 0.1}
            >
              <FeatureCard feature={feature} />
            </BlurFade>
          ))}
        </div>

        {/* Row 2: 2 cards */}
        <div className="grid grid-cols-1 gap-6 mb-6 md:grid-cols-2">
          {row2.map((feature, index) => (
            <BlurFade
              key={index}
              inView
              delay={0.4 + index * 0.1}
            >
              <FeatureCard feature={feature} />
            </BlurFade>
          ))}
        </div>

        {/* Row 3: 3 cards for 8-card layout, 2 cards for default */}
        <div
          className={`grid grid-cols-1 md:grid-cols-2 ${is8CardLayout ? 'lg:grid-cols-3' : ''} gap-6`}
        >
          {row3.map((feature, index) => (
            <BlurFade
              key={index}
              inView
              delay={0.6 + index * 0.1}
            >
              <FeatureCard feature={feature} />
            </BlurFade>
          ))}
        </div>

        {/* Call to Action */}
        <BlurFade
          inView
          delay={0.3}
        >
          <div className="mt-10 text-center md:mt-12 lg:mt-15">
            <Button
              href="#pricing"
              size="lg"
              className="px-10 text-base transition-all duration-300 lg:text-lg hover-scale hover:shadow-lg"
            >
              Activate Your Trial
            </Button>
          </div>
        </BlurFade>
      </div>
    </section>
  );
};

export default AIPoweredFeaturesSection;

// Re-export types for backwards compatibility
export type { FeatureItem as AIPoweredFeatureItem } from '~/types/feature-type';
